// zKeysManager.js
// Firebase-only Keys Sync Manager – Exclusively syncs keys from Firebase and refreshes on login
// Encrypts individual key values before sending and decrypts them upon retrieval using CryptoUtils

import { authManager } from './AuthManager';
import { getFirestore, doc, deleteDoc, setDoc, getDoc } from 'firebase/firestore';
import { app } from '@/firebase/config';
import { CryptoUtils } from '@/utils/cryptoUtils';
import { botManager } from './BotManager';

class KeyStateManagerClass {
    constructor() {
        this._subscribers = new Set();
        this._state = {
            keys: null,
            isKeysReady: false,
            isLoading: false,
            error: null,
            hasKeys: false,
            syncStatus: 'pending'
        };
        this._createConversationCallback = null; // Callback function
        // Now exclusively uses Firebase; any broadcast subscriptions have been removed.
        this.firestore = getFirestore(app);
    }

    // Set the callback function for creating conversations.
    setCreateConversationCallback(callback) {
      this._createConversationCallback = callback;
    }

    // Refresh keys from Firebase on login or state change and decrypt each key independently
    async refreshFromDB() {
        const user = authManager.getCurrentUser();
        if (!user?.uid) {
            this._updateState({
                keys: null,
                isKeysReady: true,
                isLoading: false,
                hasKeys: false,
                syncStatus: 'synced'
            });
            return false;
        }
        
        try {
            this._updateState({ isLoading: true, syncStatus: 'syncing' });
            
            const keysDocSnapshot = await getDoc(doc(this.firestore, `users/${user.uid}/secure/keys`));
            let keys = null;
            if (keysDocSnapshot.exists()) { 
                keys = keysDocSnapshot.data();
                
                // Decrypt each key (except the timestamp) individually
                const decryptedKeys = { timestamp: keys.timestamp };
                await Promise.all(
                    Object.keys(keys).map(async (k) => {
                        if (k !== 'timestamp') {
                            try {
                                decryptedKeys[k] = await CryptoUtils.decrypt(keys[k], user.uid);
                            } catch (decryptError) {
                                // Skip this key but continue with others
                            }
                        }
                    })
                );
                keys = decryptedKeys;
            }
            
            const hasKeys =
                keys &&
                typeof keys === 'object' &&
                Object.keys(keys).some((key) => key !== 'timestamp');

            this._updateState({
                keys: hasKeys ? keys : null,
                isKeysReady: true,
                isLoading: false,
                error: null,
                hasKeys,
                syncStatus: 'synced'
            });

            return hasKeys;
        } catch (error) {
            this._updateState({
                keys: null,
                isKeysReady: true,
                isLoading: false,
                hasKeys: false,
                syncStatus: 'error',
                error: error.message
            });
            return false;
        }
    }

    // Update individual keys using Firebase syncing—merges new keys into the existing keys
    // Only encrypts the values for the keys being updated without affecting other keys.
    async updateKeys(newKeys) {
        const user = authManager.getCurrentUser();
        if (!user?.uid) {
            throw new Error('No user ID available');
        }
        if (!newKeys || typeof newKeys !== 'object') {
            return false;
        }
        try {
            this._updateState({ isLoading: true, syncStatus: 'syncing' });
            const keysRef = doc(this.firestore, `users/${user.uid}/secure/keys`);
            // Fetch current keys (if any) so we can merge without affecting the others.
            const currentSnap = await getDoc(keysRef);
            let currentKeys = {};
            if (currentSnap.exists()) {
                currentKeys = currentSnap.data();
            }
            // Update each provided key independently: encrypt its value and merge into current keys.
            for (const key in newKeys) {
                // Pass true as the third argument to force encryption, bypassing the 'isEncrypted' check.
                // This is suitable here as 'updateKeys' is typically called after a key has been
                // validated (e.g., by an API test) and is assumed to be plaintext.
                currentKeys[key] = await CryptoUtils.encrypt(newKeys[key], user.uid, true);
            }
            currentKeys.timestamp = Date.now();
            await setDoc(keysRef, currentKeys);
            await this.refreshFromDB();
            this._updateState({ syncStatus: 'synced' });

            // Find the default bot and its variation.
            const defaultBotInfo = botManager.findDefaultBot();

            // Create a new conversation after updating keys, only if a default bot variation is available.
            if (this._createConversationCallback && defaultBotInfo && defaultBotInfo.variation) {
              await this._createConversationCallback(defaultBotInfo.variation.id);
            }

            return true;
        } catch (error) {
            this._updateState({
                error: error.message,
                isLoading: false,
                syncStatus: 'error'
            });
            throw error;
        }
    }

    // Delete a single key from Firebase and update state accordingly without affecting other keys
    async deleteKey(providerId) {
        const user = authManager.getCurrentUser();
        if (!user?.uid) {
            throw new Error('No user ID available');
        }
        const keysSnap = await getDoc(doc(this.firestore, `users/${user.uid}/secure/keys`));
        const currentKeys = (keysSnap.exists() && keysSnap.data()) || {};
        if (!currentKeys.hasOwnProperty(providerId)) {
            return true;
        }
        // Remove the specified key without impacting others.
        const updatedKeys = {};
        Object.keys(currentKeys).forEach((key) => {
            if (key !== providerId && key !== 'timestamp') {
                updatedKeys[key] = currentKeys[key];
            }
        });
        updatedKeys.timestamp = Date.now();
        this._updateState({ isLoading: true, syncStatus: 'syncing' });
        
        try {
            const keysRef = doc(this.firestore, `users/${user.uid}/secure/keys`);
            // If no keys remain besides the timestamp, delete the document entirely
            if (Object.keys(updatedKeys).length === 1 && updatedKeys.timestamp) {
                await deleteDoc(keysRef);
            } else {
                await setDoc(keysRef, updatedKeys);
            }

            await this.refreshFromDB();
            this._updateState({ syncStatus: 'synced' });
            return true;
        } catch (error) {
            this._updateState({
                error: error.message,
                isLoading: false,
                syncStatus: 'error'
            });
            throw error;
        }
    }

    // Manually set the sync status
    setSyncStatus(status) {
        this._updateState({ syncStatus: status });
    }

    // Internal state updater
    _updateState(newState) {
        this._state = { ...this._state, ...newState };
        this._notifySubscribers();
    }

    // Notify subscribers of state changes
    _notifySubscribers() {
        this._subscribers.forEach((callback) =>
            callback({ ...this._state, currentUser: authManager.getCurrentUser() })
        );
    }

    // Subscribe to state changes
    subscribe(callback) {
        this._subscribers.add(callback);
        callback(this.getState());
        return () => this._subscribers.delete(callback);
    }

    // Get the current state
    getState() {
        return { ...this._state, currentUser: authManager.getCurrentUser() };
    }

    // Getter for keys
    getKeys() {
        return this._state.keys;
    }

    // Whether keys are ready
    isReady() {
        return this._state.isKeysReady;
    }

    // Whether valid keys exist
    hasValidKeys() {
        return this._state.hasKeys;
    }

    // Get current error if any
    getError() {
        return this._state.error;
    }

    // Get current sync status
    getSyncStatus() {
        return this._state.syncStatus;
    }

    // Cleanup function if needed
    cleanup() {
        // No broadcast subscriptions to clean up anymore
    }
}

export const keyStateManager = new KeyStateManagerClass();